import c from 'classnames';
import Image from 'next/image';
import { Image as ImageType } from '../../../lib/types';
import { ButtonStyle } from '../../atoms/Button/Button.types';
import { Label } from '../../atoms/Label/Label';
import { LinkButton } from '../../atoms/Link/LinkButton';

export type ContentCardProps = {
  title?: string;
  id: string;
  description: string;
  image?: ImageType;
  cta?: {
    label: string;
    url: string;
    openInNewTab: boolean;
  };
  ctaStyle?: ButtonStyle;
  label?: string;
  isDarkTheme?: boolean;
  onCtaClick: (ctaLabel: string) => void;
};

export const ContentCard = ({
  title,
  id,
  description,
  image,
  cta,
  ctaStyle = 'secondary',
  label,
  isDarkTheme = false,
  onCtaClick,
}: ContentCardProps) => {
  return (
    <div className="h-full">
      <article
        className={c(
          'flex h-full flex-col rounded-3xl border',
          isDarkTheme
            ? 'border-grey-800 bg-grey-900 lg:hover:shadow-cardDark'
            : 'border-line bg-surface lg:hover:shadow-card dark:lg:hover:shadow-cardDark'
        )}
      >
        {image && (
          <div className="relative aspect-3/2 overflow-hidden rounded-3xl rounded-b-none lg:mx-6 lg:mt-6 lg:rounded-lg lg:rounded-b-lg">
            <Image
              src={image.url}
              alt={image.alt ?? ''}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="object-cover"
            />
          </div>
        )}
        <div
          className={c('inner flex grow flex-col items-baseline px-6 pb-8', {
            'pt-6': image,
            'pt-8': !image,
          })}
        >
          {label && (
            <Label
              isDarkTheme={isDarkTheme}
              appearance="highlight"
              text={label}
              className="mb-5 lg:mb-6"
            />
          )}
          {title && (
            <h3
              className={c(
                'header-xsmall mb-6 inline-block',
                isDarkTheme && 'text-white'
              )}
              id={id}
            >
              {title}
            </h3>
          )}
          <div
            className={c(
              'body-small',
              isDarkTheme ? 'text-grey-300' : 'text-content-subtle'
            )}
          >
            {description}
          </div>
          {cta && (
            <div className="mt-auto pt-8">
              <LinkButton
                size="small"
                label={cta.label}
                className="inline-block"
                href={cta.url}
                openLinkInNewTab={cta.openInNewTab}
                style={ctaStyle}
                aria-describedby={id}
                isDarkMode={isDarkTheme}
                onClick={() => onCtaClick(cta.label)}
                ariaLabel={`${title} - ${cta.label}`}
              />
            </div>
          )}
        </div>
      </article>
    </div>
  );
};
